/**
 * Verification cases.
 *
 * READ THIS BEFORE CHANGING ANY `status` FIELD.
 *
 * `status: 'development'`
 *   The expected value was produced by the same author as the implementation,
 *   during development. It demonstrates that the code does what its author
 *   intended. It does NOT demonstrate independent verification, and must never
 *   be described as validated, verified, qualified, or compliant.
 *
 * `status: 'independent'`
 *   The expected value was produced by a separate party using a calculation
 *   method outside this codebase, and is traceable to a named source. Only
 *   cases carrying this status may be counted in any "independently verified"
 *   total.
 *
 * Every case below is currently `development`. The expected values were
 * computed with a separate Python (float64) script during development. That is
 * a different implementation path from the production TypeScript, which is
 * useful, but it is not independent: one author, one session, one set of
 * assumptions. The honest description is "development test", and that is what
 * the interface displays.
 *
 * To import independently generated cases later, produce JSON or CSV matching
 * the `VerificationCase` shape, set `status: 'independent'`, and record the
 * real source in `verificationSource`. No application logic needs to change.
 */

import type { VerificationCase } from '../types';

/** Absolute tolerances for float comparison. Tight, because this is pure arithmetic. */
const AAF_TOLERANCE = 1e-9;
const AAT_TOLERANCE = 1e-9;

const DEV_METHOD =
  'Independent Python float64 script run during development by the same author as the implementation. Not third-party verification.';
const PENDING = 'pending independent verification';

function devCase(
  caseId: string,
  description: string,
  realTimeDays: number,
  realTimeTempC: number,
  acceleratedTempC: number,
  q10: number,
  expectedAAF: number,
  expectedAATDays: number,
): VerificationCase {
  return {
    caseId,
    description,
    realTimeDays,
    realTimeTempC,
    acceleratedTempC,
    q10,
    expectedAAF,
    expectedAATDays,
    toleranceAAF: AAF_TOLERANCE,
    toleranceAATDays: AAT_TOLERANCE,
    verificationSource: PENDING,
    expectedValueMethod: DEV_METHOD,
    status: 'development',
  };
}

export const VERIFICATION_CASES: VerificationCase[] = [
  devCase('AA-001', 'Worked example: 3 years, 55 °C vs 25 °C, Q10 = 2', 1095, 25, 55, 2.0, 8.0, 136.875),
  devCase('AA-002', 'Q10 variation: Q10 = 1.8', 1095, 25, 55, 1.8, 5.832000000000001, 187.75720164609052),
  devCase('AA-003', 'Q10 variation: Q10 = 2.5', 1095, 25, 55, 2.5, 15.625, 70.08),
  devCase('AA-004', 'Boundary: Q10 = 1 gives AAF = 1 at any temperature', 1095, 25, 55, 1.0, 1.0, 1095.0),
  devCase('AA-005', 'Reference temperature variation: 20 °C', 1095, 20, 55, 2.0, 11.313708498984761, 96.78524067490869),
  devCase('AA-006', 'Reference temperature variation: 30 °C', 1095, 30, 55, 2.0, 5.656854249492381, 193.57048134981738),
  devCase('AA-007', 'Accelerated temperature variation: 50 °C', 1095, 25, 50, 2.0, 5.656854249492381, 193.57048134981738),
  devCase('AA-008', 'Accelerated temperature variation: 60 °C', 1095, 25, 60, 2.0, 11.313708498984761, 96.78524067490869),
  devCase('AA-009', 'Non-integer exponent: 57 °C vs 23 °C', 1095, 23, 57, 2.0, 10.556063286183154, 103.73185252055538),
  devCase('AA-010', 'Shelf life variation: 1 year', 365, 25, 55, 2.0, 8.0, 45.625),
  devCase('AA-011', 'Shelf life variation: 5 years', 1825, 25, 55, 2.0, 8.0, 228.125),
  devCase('AA-012', 'Unit conversion: 36 months converts to 1095 days', 1095, 25, 55, 2.0, 8.0, 136.875),
  devCase('AA-013', 'Unit conversion: 18 months converts to 547.5 days', 547.5, 25, 55, 2.0, 8.0, 68.4375),
  devCase('AA-014', 'Boundary: equal temperatures, exponent = 0', 1095, 25, 25, 2.0, 1.0, 1095.0),
  devCase('AA-015', 'Boundary: inverted temperatures, AAF < 1', 1095, 55, 25, 2.0, 0.125, 8760.0),
  devCase('AA-016', 'Fractional Q10: 2.25', 1095, 25, 55, 2.25, 11.390625, 96.13168724279835),
  devCase('AA-017', 'Negative reference temperature: −10 °C', 1095, -10, 40, 2.0, 32.0, 34.21875),
  devCase('AA-018', 'Small temperature difference: 1 °C', 1095, 25, 26, 2.0, 1.0717734625362931, 1021.6711257328042),
  devCase('AA-019', 'Large temperature difference: 60 °C', 1095, 25, 85, 2.0, 64.0, 17.109375),
  devCase('AA-020', 'Boundary: Q10 below 1', 1095, 25, 55, 0.5, 0.125, 8760.0),
  devCase('AA-021', 'Short shelf life: 30 days', 30, 25, 55, 2.0, 8.0, 3.75),
  devCase('AA-022', 'Rounding: exact whole-day result, ceiling leaves it unchanged', 1000, 25, 55, 2.0, 8.0, 125.0),
  devCase('AA-023', 'Fractional temperatures: 54.5 °C vs 22.5 °C', 1095, 22.5, 54.5, 2.0, 9.18958683997628, 119.15660835115698),
  devCase('AA-024', 'Ten-year shelf life at 45 °C', 3650, 25, 45, 2.0, 4.0, 912.5),
];

/**
 * Categories the verification framework is intended to cover. Displayed so a
 * reader can see coverage intent as well as pass counts. Invalid-input and
 * date-arithmetic behaviour is exercised by the automated unit tests rather
 * than by numeric expected-value cases, and is labelled as such.
 */
export const VERIFICATION_COVERAGE: { category: string; covered: string }[] = [
  { category: 'Q10 variation', covered: 'AA-002, AA-003, AA-004, AA-016, AA-020' },
  { category: 'Reference temperature variation', covered: 'AA-005, AA-006, AA-017' },
  { category: 'Accelerated temperature variation', covered: 'AA-007, AA-008, AA-018, AA-019' },
  { category: 'Desired shelf-life variation', covered: 'AA-010, AA-011, AA-021, AA-024' },
  { category: 'Unit conversion', covered: 'AA-012, AA-013, plus unit tests in tests/units.test.ts' },
  { category: 'Rounding behaviour', covered: 'AA-022, plus unit tests in tests/acceleratedAging.test.ts' },
  { category: 'Boundary inputs', covered: 'AA-004, AA-014, AA-015, AA-018, AA-020' },
  { category: 'Non-integer exponents', covered: 'AA-009, AA-023' },
  { category: 'Date calculation', covered: 'Unit tests in tests/units.test.ts (no numeric AAF/AAT expected value applies)' },
  { category: 'Invalid input handling', covered: 'Unit tests in tests/validation.test.ts (errors vs. warnings)' },
];

/**
 * Placeholder for a future independently generated dataset.
 * Populate via `loadIndependentCases()` once real data exists.
 */
export const INDEPENDENT_CASES: VerificationCase[] = [];

/**
 * Parse externally supplied cases (JSON already parsed into objects).
 * Any case that does not carry a concrete, non-placeholder verification source
 * is downgraded to `development`, so an imported file cannot accidentally
 * inflate the independent count.
 */
export function loadIndependentCases(raw: unknown[]): VerificationCase[] {
  const out: VerificationCase[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const c = item as Partial<VerificationCase>;
    const numeric = [c.realTimeDays, c.realTimeTempC, c.acceleratedTempC, c.q10, c.expectedAAF, c.expectedAATDays];
    if (!c.caseId || numeric.some((n) => typeof n !== 'number' || !Number.isFinite(n))) continue;

    const source = (c.verificationSource ?? '').trim();
    const hasRealSource = source !== '' && !/pending|tbd|unknown|n\/?a/i.test(source);

    out.push({
      caseId: c.caseId,
      description: c.description ?? '',
      realTimeDays: c.realTimeDays as number,
      realTimeTempC: c.realTimeTempC as number,
      acceleratedTempC: c.acceleratedTempC as number,
      q10: c.q10 as number,
      expectedAAF: c.expectedAAF as number,
      expectedAATDays: c.expectedAATDays as number,
      toleranceAAF: c.toleranceAAF ?? AAF_TOLERANCE,
      toleranceAATDays: c.toleranceAATDays ?? AAT_TOLERANCE,
      verificationSource: hasRealSource ? source : PENDING,
      expectedValueMethod: c.expectedValueMethod ?? 'not stated',
      status: hasRealSource && c.status === 'independent' ? 'independent' : 'development',
    });
  }
  return out;
}

export function allVerificationCases(): VerificationCase[] {
  return [...VERIFICATION_CASES, ...INDEPENDENT_CASES];
}
